import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUser } from '../../services/user.service';
import { User, UserSettings } from '../../types/user.types';
import { toast } from 'react-hot-toast';
import {
  User as UserIcon,
  Bell,
  Shield,
  Save,
  Eye,
  EyeOff,
  Mail,
  Phone,
  Building,
  Clock,
  CreditCard,
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
} from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../../config/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { SchedulePage } from '../schedule/SchedulePage';
import {
  getCompanySettings,
  saveCompanySettings,
  initializeCompanySettings,
  addExpenseCategory,
  updateExpenseCategory,
  toggleExpenseCategoryStatus
} from '../../services/companySettings.service';
import { CompanySettings, ExpenseCategory, DEFAULT_EXPENSE_CATEGORIES } from '../../types/finance.types';
type SettingsTab = 'profile' | 'notifications' | 'schedule' | 'security' | 'company';

const SettingsPage: React.FC = () => {
  const { userProfile, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [saving, setSaving] = useState(false);

  // Profile state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  // Notification state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  const [paymentNotifications, setPaymentNotifications] = useState(true);
  const [weeklySummary, setWeeklySummary] = useState(true);

  // Company settings state (for provider_admin)
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loadingCompanySettings, setLoadingCompanySettings] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    taxId: '',
    bankName: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankRoutingNumber: '',
    bankSwiftCode: '',
    bankAddress: '',
    invoicePrefix: 'INV-',
    defaultPaymentTerms: 30,
    invoiceNotes: '',
    invoiceFooter: '',
    defaultBillRate: 45,
    defaultPayRate: 25
  });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');

  // Load company settings for provider_admin
  useEffect(() => {
    const loadCompanySettings = async () => {
      if (userProfile?.role !== 'provider_admin' || !currentUser) return;

      setLoadingCompanySettings(true);
      try {
        let settings = await getCompanySettings();
        if (!settings) {
          settings = await initializeCompanySettings(currentUser.uid);
        }
        setCompanySettings(settings);
        setCompanyForm({
          companyName: settings.companyName || '',
          companyAddress: settings.companyAddress || '',
          companyCity: settings.companyCity || '',
          companyState: settings.companyState || '',
          companyZip: settings.companyZip || '',
          companyPhone: settings.companyPhone || '',
          companyEmail: settings.companyEmail || '',
          companyWebsite: settings.companyWebsite || '',
          taxId: settings.taxId || '',
          bankName: settings.bankName || '',
          bankAccountName: settings.bankAccountName || '',
          bankAccountNumber: settings.bankAccountNumber || '',
          bankRoutingNumber: settings.bankRoutingNumber || '',
          bankSwiftCode: settings.bankSwiftCode || '',
          bankAddress: settings.bankAddress || '',
          invoicePrefix: settings.invoicePrefix || 'INV-',
          defaultPaymentTerms: settings.defaultPaymentTerms || 30,
          invoiceNotes: settings.invoiceNotes || '',
          invoiceFooter: settings.invoiceFooter || '',
          defaultBillRate: settings.defaultBillRate || 45,
          defaultPayRate: settings.defaultPayRate || 25
        });
      } catch (error) {
        console.error('Error loading company settings:', error);
      } finally {
        setLoadingCompanySettings(false);
      }
    };

    loadCompanySettings();
  }, [userProfile?.role, currentUser]);

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name || '');
      setPhone(userProfile.phoneNumber || '');

      // Load notification settings if they exist
      const settings = userProfile.settings as UserSettings | undefined;
      if (settings?.notifications) {
        setEmailNotifications(settings.notifications.email ?? true);
        setSmsNotifications(settings.notifications.sms ?? false);
        setInAppNotifications(settings.notifications.inApp ?? true);
      }
      if (settings?.billing) {
        setPaymentNotifications(settings.billing.receivePaymentNotifications ?? true);
        setWeeklySummary(settings.billing.receiveWeeklySummary ?? true);
      }
    }
  }, [userProfile]);

  const handleSaveProfile = async () => {
    if (!userProfile) return;

    setSaving(true);
    try {
      await updateUser(userProfile.uid, {
        name,
        phoneNumber: phone
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!userProfile || !currentUser) return;

    setSaving(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        settings: {
          ...userProfile.settings,
          notifications: {
            email: emailNotifications,
            sms: smsNotifications,
            inApp: inAppNotifications
          },
          billing: {
            receivePaymentNotifications: paymentNotifications,
            receiveWeeklySummary: weeklySummary
          }
        }
      });
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Error saving notifications:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!currentUser?.email) return;

    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error('Failed to send password reset email');
    }
  };

  // Company settings handlers
  const handleSaveCompanyInfo = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const updated = await saveCompanySettings({
        companyName: companyForm.companyName,
        companyAddress: companyForm.companyAddress,
        companyCity: companyForm.companyCity,
        companyState: companyForm.companyState,
        companyZip: companyForm.companyZip,
        companyPhone: companyForm.companyPhone,
        companyEmail: companyForm.companyEmail,
        companyWebsite: companyForm.companyWebsite,
        taxId: companyForm.taxId
      }, currentUser.uid);
      setCompanySettings(updated);
    } catch (error) {
      console.error('Error saving company info:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBankDetails = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const updated = await saveCompanySettings({
        bankName: companyForm.bankName,
        bankAccountName: companyForm.bankAccountName,
        bankAccountNumber: companyForm.bankAccountNumber,
        bankRoutingNumber: companyForm.bankRoutingNumber,
        bankSwiftCode: companyForm.bankSwiftCode,
        bankAddress: companyForm.bankAddress
      }, currentUser.uid);
      setCompanySettings(updated);
    } catch (error) {
      console.error('Error saving bank details:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInvoiceSettings = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const updated = await saveCompanySettings({
        invoicePrefix: companyForm.invoicePrefix,
        defaultPaymentTerms: companyForm.defaultPaymentTerms,
        invoiceNotes: companyForm.invoiceNotes,
        invoiceFooter: companyForm.invoiceFooter,
        defaultBillRate: companyForm.defaultBillRate,
        defaultPayRate: companyForm.defaultPayRate
      }, currentUser.uid);
      setCompanySettings(updated);
      toast.success('Invoice settings saved');
    } catch (error) {
      console.error('Error saving invoice settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddExpenseCategory = async () => {
    if (!currentUser || !newCategoryName.trim()) return;

    try {
      await addExpenseCategory({
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim(),
        isActive: true
      }, currentUser.uid);

      // Reload settings
      const settings = await getCompanySettings();
      setCompanySettings(settings);
      setNewCategoryName('');
      setNewCategoryDescription('');
    } catch (error) {
      console.error('Error adding expense category:', error);
    }
  };

  const handleUpdateExpenseCategory = async (categoryId: string) => {
    if (!currentUser || !editCategoryName.trim()) return;

    try {
      await updateExpenseCategory(categoryId, { name: editCategoryName.trim() }, currentUser.uid);

      // Reload settings
      const settings = await getCompanySettings();
      setCompanySettings(settings);
      setEditingCategory(null);
      setEditCategoryName('');
    } catch (error) {
      console.error('Error updating expense category:', error);
    }
  };

  const handleToggleCategoryStatus = async (categoryId: string) => {
    if (!currentUser) return;

    try {
      await toggleExpenseCategoryStatus(categoryId, currentUser.uid);

      // Reload settings
      const settings = await getCompanySettings();
      setCompanySettings(settings);
    } catch (error) {
      console.error('Error toggling category status:', error);
    }
  };

  // Determine which tabs to show based on user role
  const isProviderAdmin = userProfile?.role === 'provider_admin';

  const baseTabs = [
    { id: 'profile' as SettingsTab, name: 'Profile', icon: UserIcon },
    { id: 'notifications' as SettingsTab, name: 'Notifications', icon: Bell },
    { id: 'schedule' as SettingsTab, name: 'Schedule', icon: Clock },
    { id: 'security' as SettingsTab, name: 'Security', icon: Shield }
  ];

  const tabs = isProviderAdmin
    ? [
        ...baseTabs,
        { id: 'company' as SettingsTab, name: 'Company', icon: Building }
      ]
    : baseTabs;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab.name}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Profile Information</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <UserIcon className="h-4 w-4 inline mr-1" />
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="h-4 w-4 inline mr-1" />
                Email
              </label>
              <input
                type="email"
                value={userProfile?.email || ''}
                disabled
                className="w-full px-3 py-2 border rounded-md bg-gray-100 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="h-4 w-4 inline mr-1" />
                Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Building className="h-4 w-4 inline mr-1" />
                Organization
              </label>
              <input
                type="text"
                value={userProfile?.organization || ''}
                disabled
                className="w-full px-3 py-2 border rounded-md bg-gray-100 text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                {userProfile?.role?.replace('_', ' ')}
              </span>
            </div>

            <button
              onClick={handleSaveProfile}
              disabled={saving}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Notification Preferences</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">Communication Channels</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Email notifications</span>
                  <input
                    type="checkbox"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </label>

                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">SMS notifications</span>
                  <input
                    type="checkbox"
                    checked={smsNotifications}
                    onChange={(e) => setSmsNotifications(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </label>

                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">In-app notifications</span>
                  <input
                    type="checkbox"
                    checked={inAppNotifications}
                    onChange={(e) => setInAppNotifications(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </label>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Billing & Payments</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Payment notifications</span>
                  <input
                    type="checkbox"
                    checked={paymentNotifications}
                    onChange={(e) => setPaymentNotifications(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </label>

                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Weekly summary email</span>
                  <input
                    type="checkbox"
                    checked={weeklySummary}
                    onChange={(e) => setWeeklySummary(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </label>
              </div>
            </div>

            <button
              onClick={handleSaveNotifications}
              disabled={saving}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>
      )}

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <SchedulePage />
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Security Settings</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Password</h3>
              <p className="text-sm text-gray-500 mb-3">
                To change your password, we'll send a reset link to your email address.
              </p>
              <button
                onClick={handlePasswordReset}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
              >
                Send Password Reset Email
              </button>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Account Information</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Account created: {userProfile?.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                <p>Last login: {userProfile?.lastLogin?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                <p>User ID: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{userProfile?.uid}</code></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Tab (Provider Admin Only) */}
      {activeTab === 'company' && isProviderAdmin && (
        <div className="space-y-6">
          {loadingCompanySettings ? (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <p className="text-gray-500">Loading company settings...</p>
            </div>
          ) : (
            <>
              {/* Company Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Building className="h-5 w-5 mr-2" />
                  Company Information
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  This information will appear on invoices and other documents.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={companyForm.companyName}
                      onChange={(e) => setCompanyForm({ ...companyForm, companyName: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Your Company Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax ID / EIN
                    </label>
                    <input
                      type="text"
                      value={companyForm.taxId}
                      onChange={(e) => setCompanyForm({ ...companyForm, taxId: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="XX-XXXXXXX"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={companyForm.companyAddress}
                      onChange={(e) => setCompanyForm({ ...companyForm, companyAddress: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={companyForm.companyCity}
                      onChange={(e) => setCompanyForm({ ...companyForm, companyCity: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="City"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={companyForm.companyState}
                        onChange={(e) => setCompanyForm({ ...companyForm, companyState: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="CA"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        value={companyForm.companyZip}
                        onChange={(e) => setCompanyForm({ ...companyForm, companyZip: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="12345"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={companyForm.companyPhone}
                      onChange={(e) => setCompanyForm({ ...companyForm, companyPhone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={companyForm.companyEmail}
                      onChange={(e) => setCompanyForm({ ...companyForm, companyEmail: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="billing@yourcompany.com"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website (optional)
                    </label>
                    <input
                      type="url"
                      value={companyForm.companyWebsite}
                      onChange={(e) => setCompanyForm({ ...companyForm, companyWebsite: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="https://yourcompany.com"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSaveCompanyInfo}
                  disabled={saving}
                  className="mt-4 flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Company Info'}
                </button>
              </div>

              {/* Bank/Wire Details */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2" />
                  Bank & Wire Details
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  These details will be displayed on invoices for client payments.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      value={companyForm.bankName}
                      onChange={(e) => setCompanyForm({ ...companyForm, bankName: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="First National Bank"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Name
                    </label>
                    <input
                      type="text"
                      value={companyForm.bankAccountName}
                      onChange={(e) => setCompanyForm({ ...companyForm, bankAccountName: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Your Company LLC"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={companyForm.bankAccountNumber}
                      onChange={(e) => setCompanyForm({ ...companyForm, bankAccountNumber: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="XXXXXXXXXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Routing Number
                    </label>
                    <input
                      type="text"
                      value={companyForm.bankRoutingNumber}
                      onChange={(e) => setCompanyForm({ ...companyForm, bankRoutingNumber: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="XXXXXXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SWIFT Code (optional, for international)
                    </label>
                    <input
                      type="text"
                      value={companyForm.bankSwiftCode}
                      onChange={(e) => setCompanyForm({ ...companyForm, bankSwiftCode: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="XXXXXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Address (optional)
                    </label>
                    <input
                      type="text"
                      value={companyForm.bankAddress}
                      onChange={(e) => setCompanyForm({ ...companyForm, bankAddress: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="123 Bank St, City, State"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSaveBankDetails}
                  disabled={saving}
                  className="mt-4 flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Bank Details'}
                </button>
              </div>

              {/* Invoice Settings */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Invoice Settings</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Configure default settings for new invoices.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Number Prefix
                    </label>
                    <input
                      type="text"
                      value={companyForm.invoicePrefix}
                      onChange={(e) => setCompanyForm({ ...companyForm, invoicePrefix: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="INV-"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Next invoice: {companyForm.invoicePrefix}{new Date().getFullYear()}-{String(companySettings?.nextInvoiceNumber || 1).padStart(4, '0')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Payment Terms (days)
                    </label>
                    <input
                      type="number"
                      value={companyForm.defaultPaymentTerms}
                      onChange={(e) => setCompanyForm({ ...companyForm, defaultPaymentTerms: parseInt(e.target.value) || 30 })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      min="1"
                      max="365"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Client Bill Rate ($)
                    </label>
                    <input
                      type="number"
                      value={companyForm.defaultBillRate}
                      onChange={(e) => setCompanyForm({ ...companyForm, defaultBillRate: parseFloat(e.target.value) || 45 })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-gray-500 mt-1">Default rate charged to clients per valid lead</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Field Pay Rate ($)
                    </label>
                    <input
                      type="number"
                      value={companyForm.defaultPayRate}
                      onChange={(e) => setCompanyForm({ ...companyForm, defaultPayRate: parseFloat(e.target.value) || 25 })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-gray-500 mt-1">Default rate paid to field staff per valid lead</p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Invoice Notes
                    </label>
                    <textarea
                      value={companyForm.invoiceNotes}
                      onChange={(e) => setCompanyForm({ ...companyForm, invoiceNotes: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      rows={2}
                      placeholder="Thank you for your business!"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Footer Text
                    </label>
                    <textarea
                      value={companyForm.invoiceFooter}
                      onChange={(e) => setCompanyForm({ ...companyForm, invoiceFooter: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      rows={2}
                      placeholder="Payment is due within the specified terms."
                    />
                  </div>
                </div>

                <button
                  onClick={handleSaveInvoiceSettings}
                  disabled={saving}
                  className="mt-4 flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Invoice Settings'}
                </button>
              </div>

              {/* Expense Categories */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Expense Categories</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Manage expense categories for tracking project costs.
                </p>

                {/* Add new category */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Add New Category</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="Category name"
                    />
                    <input
                      type="text"
                      value={newCategoryDescription}
                      onChange={(e) => setNewCategoryDescription(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="Description (optional)"
                    />
                    <button
                      onClick={handleAddExpenseCategory}
                      disabled={!newCategoryName.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Category list */}
                <div className="space-y-2">
                  {companySettings?.expenseCategories?.map((category) => (
                    <div
                      key={category.id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        !category.isActive ? 'bg-gray-50 opacity-60' : ''
                      }`}
                    >
                      <div className="flex-1">
                        {editingCategory === category.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editCategoryName}
                              onChange={(e) => setEditCategoryName(e.target.value)}
                              className="flex-1 px-2 py-1 border rounded text-sm"
                              autoFocus
                            />
                            <button
                              onClick={() => handleUpdateExpenseCategory(category.id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingCategory(null);
                                setEditCategoryName('');
                              }}
                              className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="font-medium text-sm">{category.name}</span>
                            {category.description && (
                              <span className="text-xs text-gray-500 ml-2">- {category.description}</span>
                            )}
                          </>
                        )}
                      </div>

                      {editingCategory !== category.id && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingCategory(category.id);
                              setEditCategoryName(category.name);
                            }}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            title="Edit"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleCategoryStatus(category.id)}
                            className={`px-2 py-1 text-xs rounded ${
                              category.isActive
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {category.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
};

export default SettingsPage;
